import { useMemo } from 'react';

import {
  DrawerContent,
  notify,
  Typography,
  useDrawerInstance,
  useSwipeableViews,
  tokens,
} from '@cast/design-system';
import { RffForm } from '@cast/shared-rff-ui';
import { makeValidateSync } from '@cast/utils';
import * as yup from 'yup';

import { useCreateEdgeLocationMutation } from 'hooks/mutations/omni';
import { useListAddonsQuery } from 'hooks/queries/omni';

import { EdgeLocationDrawerFooter } from '../../_components/EdgeLocationDrawerFooter';
import {
  SelectAddonsField,
  edgeLocationProviderToAddonsApiProvider,
} from '../../_components/SelectAddonsField';
import {
  ControlWithDescription,
  InlineWithDescription,
} from 'components/form-layouts';

import { isOmniAddonCompatible } from '../../_utils/isOmniAddonCompatible';
import { formModelToApi } from '../../_utils';
import { formModelAddonsSchema } from '../../_validation';
import { useGetOmniClusterFromList } from '../useGetOmniClusterFromList';
import { CUSTOM_EDGE_PROVIDER } from '../../constants';
import { EdgeLocationAddonName, EdgeLocationFormValues } from '../../types';

type SelectAddonsStepValues = Pick<EdgeLocationFormValues, 'addons'>;

const validate = makeValidateSync(
  yup.object({ addons: formModelAddonsSchema })
);

type SelectAddonsStepProps = {
  clusterId: string;
  formValues: EdgeLocationFormValues;
  hideSuccessNotification?: boolean;
  onCreateLocationSuccess: (edgeLocationId: string) => void;
};

const DEFAULT_ADDONS = [
  EdgeLocationAddonName.NvidiaGpuOperator,
  EdgeLocationAddonName.NvidiaDra,
];

export const SelectAddonsStep = ({
  clusterId,
  formValues,
  hideSuccessNotification = false,
  onCreateLocationSuccess,
}: SelectAddonsStepProps) => {
  const isCustomProvider = formValues.provider === CUSTOM_EDGE_PROVIDER;

  const { clusterDetails } = useGetOmniClusterFromList({ clusterId });
  const { addons } = useListAddonsQuery({
    provider: edgeLocationProviderToAddonsApiProvider(formValues.provider),
  });

  // Pre-select the default add-ons only when the cluster's omni agent is
  // new enough to support them. Incompatible defaults are dropped so that
  // the form never submits add-ons the cluster can't run.
  const initialValues: SelectAddonsStepValues = useMemo(() => {
    const omniAgentVersion = clusterDetails?.omniAgentVersion;
    const compatibleDefaults = DEFAULT_ADDONS.filter((name) => {
      const addon = addons.find((a) => a.name === name);
      if (!addon) return false;
      return isOmniAddonCompatible(addon, omniAgentVersion);
    });

    return {
      addons: Object.fromEntries(
        compatibleDefaults.map((addonName) => [addonName, { enabled: true }])
      ),
    };
  }, [addons, clusterDetails?.omniAgentVersion]);

  const { mutate: createEdgeLocation, isPending } =
    useCreateEdgeLocationMutation({
      clusterId,
    });
  const ctx = useSwipeableViews();
  const { close } = useDrawerInstance();

  const handleSubmit = (values: SelectAddonsStepValues) => {
    const edgeLocation = formModelToApi({
      ...formValues,
      addons: values.addons,
    });

    createEdgeLocation(edgeLocation, {
      onSuccess: ({ data: newEdgeLocation }) => {
        onCreateLocationSuccess(newEdgeLocation.id!);

        if (isCustomProvider) {
          if (!hideSuccessNotification) {
            notify.success('Edge location created successfully');
          }
          close();
          return;
        }

        ctx?.goToNextSlide();
      },
    });
  };

  return (
    <RffForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      mode="create"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      {({ handleSubmit: submitForm }) => (
        <>
          <DrawerContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Typography
              variant="L12R"
              color={tokens.theme.fg.structural.secondary}
              mb={8}
            >
              Create edge location
            </Typography>
            <Typography variant="h5" mb={12}>
              Configure new edge location
            </Typography>
            <InlineWithDescription
              gridTemplateColumns="266px auto"
              gap={42}
              py={16}
              sx={{
                borderBottom: 'none',
              }}
              description={
                <ControlWithDescription label="Select add-ons">
                  Cast AI components installed on nodes in this edge location.
                  OMNI Agent is required; the rest are optional based on your
                  workload needs.
                </ControlWithDescription>
              }
              controls={
                <SelectAddonsField
                  provider={formValues.provider}
                  name="addons"
                  omniAgentVersion={clusterDetails?.omniAgentVersion}
                />
              }
            />
          </DrawerContent>

          <EdgeLocationDrawerFooter
            submitButtonTestId="create-edge-location-with-addons-button"
            backButtonTestId="create-edge-location-addons-back-button"
            showSubmitIcon={!isCustomProvider}
            submitButtonLabel={isCustomProvider ? 'Create' : 'Next'}
            isDisabled={isPending}
            isLoading={isPending}
            onClose={close}
            onSubmit={submitForm}
            onBack={() => ctx?.goToPreviousSlide()}
          />
        </>
      )}
    </RffForm>
  );
};
